import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, Users, Plus, X, Save, ArrowLeft, GripVertical,
  Tv, Sparkles, Calendar, Hand, Contact, FileText, GraduationCap,
  Network, Wallet, Heart, ScrollText, Dog, BookOpen, Lock,
  CheckCircle, Plane, Car
} from 'lucide-react';
import { fetchFamilies } from '../services/familiesApi';
import { getCurrentUserData } from '../utils/auth';
import { getFamilyInitials } from '../utils/userUtils';
import Toast from './Toast';
import {
  fetchFamilyCategories,
  updateCategory,
  fetchCategoryPermissions,
  grantCategoryPermission,
  revokeCategoryPermission
} from '../services/categorySettingsApi';

const FamilySettings = () => {
  const navigate = useNavigate();
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [categoryPermissions, setCategoryPermissions] = useState({});
  const [newPermission, setNewPermission] = useState({ email: '', access: 'read_write' });
  const [permissionError, setPermissionError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [toast, setToast] = useState(null);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const loadFamilies = async () => {
    try {
      const data = await fetchFamilies();
      console.log('[FamilySettings] Loaded families:', data);
      setFamilies(data);

      // Try to load selected family from localStorage
      const selectedFamilyId = localStorage.getItem('selectedFamilyId');
      // Convert to string for consistent comparison
      const familyIdStr = selectedFamilyId ? String(selectedFamilyId) : null;
      if (familyIdStr && data.find(f => String(f.id) === familyIdStr)) {
        setSelectedFamily(familyIdStr);
      } else if (data.length > 0) {
        setSelectedFamily(String(data[0].id));
      }
    } catch (error) {
      console.error('Failed to load families:', error);
      setLoading(false);
      alert('Failed to load families: ' + error.message);
    }
  };

  const loadCategories = useCallback(async () => {
    if (!selectedFamily) {
      console.log('[FamilySettings] No selectedFamily, skipping loadCategories');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setPermissionError(false);
      setErrorMessage('');

      // Convert to string for consistent comparison
      const familyData = families.find(f => String(f.id) === String(selectedFamily));
      console.log('[FamilySettings] Loading categories for family:', selectedFamily);
      console.log('[FamilySettings] Family data:', familyData);
      console.log('[FamilySettings] my_access:', familyData?.my_access);
      console.log('[FamilySettings] API URL:', `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/v1/families/${selectedFamily}/categories?include_disabled=true`);

      if (!familyData) {
        console.warn('[FamilySettings] Family not found in families array');
        setPermissionError(true);
        setErrorMessage('Family not found. Please select a family from the dashboard.');
        setLoading(false);
        return;
      }

      // Include disabled categories for admin view
      const data = await fetchFamilyCategories(selectedFamily, true);
      console.log('[FamilySettings] Categories loaded successfully:', data);
      setCategories(data);
    } catch (error) {
      console.error('[FamilySettings] Failed to load categories:', error);
      console.error('[FamilySettings] Error type:', typeof error);
      console.error('[FamilySettings] Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response,
        status: error.status
      });

      // Check if it's a permission/authorization error
      if (error.message.includes('403') || error.message.includes('Forbidden') ||
        error.message.includes('permission') || error.message.includes('authorized')) {
        setPermissionError(true);
        setErrorMessage('You do not have permission to access the settings for this family. Only family admins can manage category settings.');
      } else if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
        setPermissionError(true);
        setErrorMessage('Unable to load family settings. This may be because you don\'t have admin privileges for this family, or there\'s a network issue. Please contact the family owner if you need access.');
      } else {
        setPermissionError(true);
        setErrorMessage('Failed to load family settings: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedFamily, families]);

  // Category Ordering Functions - Defined early to avoid hoisting issues
  const loadCategoryOrder = useCallback(async () => {
    if (!selectedFamily || categories.length === 0) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/v1/families/${selectedFamily}/category-order`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.category_order && Array.isArray(data.category_order)) {
          setCategoryOrder(data.category_order);
        } else {
          // If no custom order, use alphabetical
          const alphabeticalOrder = categories
            .map(cat => cat.slug)
            .sort((a, b) => {
              const catA = categories.find(c => c.slug === a);
              const catB = categories.find(c => c.slug === b);
              return (catA?.name || '').localeCompare(catB?.name || '');
            });
          setCategoryOrder(alphabeticalOrder);
        }
      }
    } catch (error) {
      console.error('Error loading category order:', error);
    }
  }, [selectedFamily, categories]);

  useEffect(() => {
    loadFamilies();
  }, []);

  useEffect(() => {
    if (selectedFamily && families.length > 0) {
      console.log('[FamilySettings] useEffect triggered - loading categories for:', selectedFamily);
      console.log('[FamilySettings] Families state:', families);
      loadCategories();
    }
  }, [selectedFamily, families, loadCategories]);

  useEffect(() => {
    if (categories.length > 0 && selectedFamily) {
      loadCategoryOrder();
    }
  }, [categories.length, selectedFamily, loadCategoryOrder]);

  const loadCategoryPermissions = async (categoryId) => {
    try {
      const data = await fetchCategoryPermissions(categoryId);
      setCategoryPermissions(prev => ({
        ...prev,
        [categoryId]: data
      }));
    } catch (error) {
      console.error('Failed to load category permissions:', error);
    }
  };

  const handleToggleCategory = async (categoryId, currentStatus) => {
    try {
      await updateCategory(categoryId, !currentStatus);
      // Update local state
      setCategories(prev => prev.map(cat =>
        cat.id === categoryId ? { ...cat, is_enabled: !currentStatus } : cat
      ));
      showToast(
        `Category ${!currentStatus ? 'enabled' : 'disabled'} successfully`,
        'success'
      );
    } catch (error) {
      console.error('Failed to toggle category:', error);
      showToast('Failed to update category: ' + error.message, 'error');
    }
  };

  const handleExpandCategory = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(categoryId);
      // Load permissions if not already loaded
      if (!categoryPermissions[categoryId]) {
        loadCategoryPermissions(categoryId);
      }
    }
  };

  const handleAddPermission = async (categoryId) => {
    if (!newPermission.email) {
      showToast('Please select a family member', 'warning');
      return;
    }

    try {
      setSaving(true);
      await grantCategoryPermission(categoryId, newPermission.email, newPermission.access);

      // Reload permissions for this category
      await loadCategoryPermissions(categoryId);

      // Reload family data to update the members list
      await loadFamilies();

      // Reset form
      setNewPermission({ email: '', access: 'read_write' });
      showToast('Permission granted successfully', 'success');
    } catch (error) {
      console.error('Failed to grant permission:', error);
      showToast('Failed to grant permission: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokePermission = async (categoryId, userId) => {
    if (!window.confirm('Are you sure you want to revoke this permission?')) {
      return;
    }

    try {
      await revokeCategoryPermission(categoryId, userId);

      // Reload permissions for this category
      await loadCategoryPermissions(categoryId);

      showToast('Permission revoked successfully', 'success');
    } catch (error) {
      console.error('Failed to revoke permission:', error);
      showToast('Failed to revoke permission: ' + error.message, 'error');
    }
  };

  const saveCategoryOrder = async () => {
    if (!selectedFamily) return;

    try {
      setSaving(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/v1/families/${selectedFamily}/category-order`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ category_order: categoryOrder })
      });

      if (response.ok) {
        showToast('Category order saved successfully!', 'success');
      } else {
        throw new Error('Failed to save category order');
      }
    } catch (error) {
      console.error('Error saving category order:', error);
      showToast('Failed to save category order', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetCategoryOrder = async () => {
    if (!selectedFamily) return;

    try {
      setSaving(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/v1/families/${selectedFamily}/category-order`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ category_order: null })
      });

      if (response.ok) {
        // Set to alphabetical order
        const alphabeticalOrder = categories
          .map(cat => cat.slug)
          .sort((a, b) => {
            const catA = categories.find(c => c.slug === a);
            const catB = categories.find(c => c.slug === b);
            return (catA?.name || '').localeCompare(catB?.name || '');
          });
        setCategoryOrder(alphabeticalOrder);
        showToast('Category order reset to alphabetical!', 'success');
      } else {
        throw new Error('Failed to reset category order');
      }
    } catch (error) {
      console.error('Error resetting category order:', error);
      showToast('Failed to reset category order', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    // Get current sorted categories to work with
    const sortedCategories = getSortedCategories();

    // Create new order based on current displayed order
    let newOrder = sortedCategories.map(cat => cat.slug);

    // Reorder: remove from draggedIndex and insert at dropIndex
    const [draggedSlug] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, draggedSlug);

    setCategoryOrder(newOrder);
    setDraggedIndex(null);

    // Auto-save the new order
    try {
      setSaving(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/v1/families/${selectedFamily}/category-order`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ category_order: newOrder })
      });

      if (response.ok) {
        showToast('Category order updated!', 'success');
      } else {
        throw new Error('Failed to save category order');
      }
    } catch (error) {
      console.error('Error saving category order:', error);
      showToast('Failed to save category order', 'error');
      // Revert on error
      loadCategoryOrder();
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Sort categories based on saved order
  const getSortedCategories = () => {
    if (!categoryOrder || categoryOrder.length === 0) {
      // Default alphabetical sort
      return [...categories].sort((a, b) => a.name.localeCompare(b.name));
    }

    const ordered = [];
    categoryOrder.forEach(slug => {
      const category = categories.find(c => c.slug === slug);
      if (category) ordered.push(category);
    });

    // Add any new categories not in the order
    const remainingCategories = categories
      .filter(cat => !categoryOrder.includes(cat.slug))
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...ordered, ...remainingCategories];
  };

  const currentUser = getCurrentUserData();
  const selectedFamilyData = families.find(f => String(f.id) === String(selectedFamily));

  // Check if user is admin (owner or has full access)
  const isAdmin = selectedFamilyData?.my_access === 'full';

  // Icon mapping - same as Dashboard
  const getCategoryIcon = (slug) => {
    const iconMap = {
      'appliances': Tv,
      'beliefs': Sparkles,
      'calendar': Calendar,
      'community': Hand,
      'contacts': Contact,
      'documents': FileText,
      'education': GraduationCap,
      'familytree': Network,
      'finance': Wallet,
      'health': Heart,
      'healthwellness': Heart,
      'legacy': ScrollText,
      'pets': Dog,
      'recipes': BookOpen,
      'secrets': Lock,
      'secretsvault': Lock,
      'todos': CheckCircle,
      'todolists': CheckCircle,
      'trips': Plane,
      'vehicles': Car
    };
    return iconMap[slug] || FileText; // Default to FileText if not found
  };

  // Debug logging
  console.log('[FamilySettings] Render state:', {
    familiesCount: families.length,
    selectedFamily,
    selectedFamilyData: selectedFamilyData?.name,
    categoriesCount: categories.length,
    loading,
    permissionError,
    errorMessage
  });

  if (selectedFamilyData) {
    console.log('[FamilySettings] Selected family:', {
      name: selectedFamilyData.name,
      owner_id: selectedFamilyData.owner_id,
      my_access: selectedFamilyData.my_access,
      current_user_id: currentUser?.id,
      isAdmin
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header - Always visible */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Family Photo */}
              {selectedFamilyData?.photo ? (
                <img
                  src={selectedFamilyData.photo}
                  alt={selectedFamilyData.name}
                  className="w-16 h-16 rounded-full object-cover shadow-lg border-4 border-white"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg border-4 border-white">
                  {getFamilyInitials(selectedFamilyData?.name || 'Family')}
                </div>
              )}

              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  {selectedFamilyData?.name || 'Family'} Settings
                </h1>
                <p className="text-gray-600 mt-1">
                  {selectedFamilyData ? (
                    <>Manage categories and permissions • Your access: <span className="font-semibold capitalize text-blue-600">{selectedFamilyData.my_access || 'loading...'}</span></>
                  ) : (
                    'Loading family information...'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Permission Error Display */}
        {permissionError && (
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-8 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-bold text-red-800 mb-2">
                  Access Denied
                </h3>
                <p className="text-red-700 mb-4">
                  {errorMessage}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Go to Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/famlis')}
                    className="px-4 py-2 bg-white text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    Manage Families
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* No Family Selected */}
        {!selectedFamily && !loading && !permissionError && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">No Family Selected</h3>
            <p className="text-gray-600 mb-6">Please select a family from the dashboard to manage its settings.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {/* Categories List */}
        {!permissionError && loading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading categories...</p>
          </div>
        ) : !permissionError && selectedFamily && categories.length > 0 ? (
          <div className="space-y-4">
            {getSortedCategories().map((category, index) => (
              <div
                key={category.id}
                draggable={isAdmin}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`bg-white rounded-lg shadow-md overflow-hidden transition-all ${!category.is_enabled ? 'opacity-60' : ''
                  } ${draggedIndex === index ? 'opacity-50' : ''} ${isAdmin ? 'cursor-move' : ''}`}
              >
                {/* Category Header */}
                <div className="p-6 flex items-center gap-4">
                  {isAdmin && (
                    <div className="flex-shrink-0">
                      <GripVertical className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        {(() => {
                          const IconComponent = getCategoryIcon(category.slug);
                          return <IconComponent className="w-8 h-8 text-primary-blue" />;
                        })()}
                        <div>
                          <h3 className="text-xl font-semibold text-gray-800">
                            {category.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {category.slug} • {category.permission_count} custom permissions
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        {/* Permissions Button */}
                        <button
                          onClick={() => handleExpandCategory(category.id)}
                          className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                          disabled={!isAdmin}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Permissions
                        </button>

                        {/* Enable/Disable Toggle Switch */}
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-medium ${category.is_enabled ? 'text-gray-700' : 'text-gray-500'}`}>
                            {category.is_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={() => handleToggleCategory(category.id, category.is_enabled)}
                            disabled={!isAdmin}
                            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${category.is_enabled ? 'bg-blue-600' : 'bg-gray-300'
                              } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span
                              className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${category.is_enabled ? 'translate-x-7' : 'translate-x-1'
                                }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Permissions Section */}
                {expandedCategory === category.id && (
                  <div className="border-t border-gray-200 bg-gray-50 p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <Users className="w-5 h-5 mr-2" />
                      Category-Specific Permissions
                    </h4>

                    {!category.is_enabled && (
                      <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800 font-medium">
                          ⚠️ This category is currently disabled. Enable the category to manage permissions.
                        </p>
                      </div>
                    )}

                    <p className="text-sm text-gray-600 mb-4">
                      These permissions override family-level permissions for this category only.
                      Users without specific permissions inherit their family-level access.
                    </p>

                    {/* Add New Permission */}
                    {isAdmin && category.is_enabled && (
                      <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                        <h5 className="text-sm font-medium text-gray-700 mb-3">
                          Grant Permission to User
                        </h5>
                        <div className="flex items-end space-x-3">
                          <div className="flex-1">
                            <label className="block text-xs text-gray-600 mb-1">
                              Select Family Member
                            </label>
                            <select
                              value={newPermission.email}
                              onChange={(e) => setNewPermission({ ...newPermission, email: e.target.value })}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="">-- Select a member --</option>
                              {selectedFamilyData?.members
                                ?.filter(member => member.email !== currentUser?.email)
                                .map(member => (
                                  <option key={member.email} value={member.email}>
                                    {member.name} ({member.email})
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div className="w-40">
                            <label className="block text-xs text-gray-600 mb-1">
                              Access Level
                            </label>
                            <select
                              value={newPermission.access}
                              onChange={(e) => setNewPermission({ ...newPermission, access: e.target.value })}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="read">Read</option>
                              <option value="read_write">Read & Write</option>
                              <option value="full">Full</option>
                            </select>
                          </div>
                          <button
                            onClick={() => handleAddPermission(category.id)}
                            disabled={saving}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Existing Permissions */}
                    {category.is_enabled && (
                      <div className="space-y-2">
                        {categoryPermissions[category.id]?.length > 0 ? (
                          categoryPermissions[category.id].map((perm) => (
                            <div
                              key={perm.id}
                              className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                            >
                              <div className="flex items-center space-x-3">
                                {perm.user_picture ? (
                                  <img
                                    src={perm.user_picture}
                                    alt={perm.user_name}
                                    className="w-10 h-10 rounded-full"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                                    {perm.user_name.charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-gray-800">{perm.user_name}</p>
                                  <p className="text-xs text-gray-500">{perm.user_email}</p>
                                </div>
                              </div>

                              <div className="flex items-center space-x-3">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${perm.access === 'full' ? 'bg-purple-100 text-purple-700' :
                                  perm.access === 'read_write' ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                  {perm.access === 'read_write' ? 'Read & Write' : perm.access.toUpperCase()}
                                </span>

                                {isAdmin && (
                                  <button
                                    onClick={() => handleRevokePermission(category.id, perm.user_id)}
                                    className="text-red-600 hover:text-red-700 p-1"
                                    title="Revoke permission"
                                  >
                                    <X className="w-5 h-5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-4">
                            No category-specific permissions set. All users inherit family-level permissions.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : !permissionError && selectedFamily && !loading && categories.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">No Categories Found</h3>
            <p className="text-gray-600 mb-6">
              Unable to load categories for this family. This may be a temporary issue.
            </p>
            <button
              onClick={() => {
                if (selectedFamily) {
                  loadCategories();
                }
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry Loading Categories
            </button>
          </div>
        ) : null}
      </div>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default FamilySettings;

